'use client';
import { motion } from 'motion/react';
import Head from 'next/head';
import React, { useEffect, useRef, useState } from 'react';

import { AnimatedBlob } from '@/components/AnimatedBlob';
import { useFrontPageEffect } from '@/contexts/FrontPageEffectContext';
import type { Hero, Media } from '@/payload-types';
import { cn } from '@/utils/cn';

import { HeroContent } from '../HeroContent';
import styles from './FrontPageHero.module.scss';
import { FrontPagePlayPauseButton } from './FrontPagePlayPauseButton';

type Props = Hero & {
  locale?: string;
  children?: React.ReactNode;
};

const isSafari = () => {
  if (typeof window === 'undefined') return false;

  const ua = window.navigator.userAgent;
  return /^((?!chrome|android).)*safari/i.test(ua);
};

export const FrontPageHero: React.FC<Props> = (props) => {
  const { backgroundColor, video, videoMp4 } = props;

  const { setVideoLoaded, videoLoaded } = useFrontPageEffect();
  const [finalRightValue, setFinalRightValue] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [startDelay, setStartDelay] = useState(3.62);
  const [isInView, setIsInView] = useState(true);
  const [userPaused, setUserPaused] = useState(false);
  const [selectedSource, setSelectedSource] = useState<Media | null>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const frontpageContainer = useRef<HTMLDivElement>(null);
  const videoElement = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const getCompatibleVideoSource = () => {
      if (typeof window === 'undefined') return null;

      const safari = isSafari();

      // On Safari, prefer mp4 if available
      if (safari && videoMp4) return videoMp4 as Media;

      // Otherwise use webm if supported
      const canUseWebm =
        document.createElement('video').canPlayType('video/webm') !== '';

      if (canUseWebm && video) return video as Media;

      // Fallback to mp4 if webm is unsupported
      if (videoMp4) return videoMp4 as Media;

      return null;
    };

    const src = getCompatibleVideoSource();
    setSelectedSource(src);
  }, [video, videoMp4]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 1279px)').matches);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (frontpageContainer.current) {
      const { right } = frontpageContainer.current.getBoundingClientRect();

      const viewportWidth =
        window.innerWidth -
        (window.innerWidth - document.documentElement.clientWidth);

      const finalRightPx = viewportWidth - right;

      // Convert to percentage of viewport width
      const finalRightPercent = (finalRightPx / viewportWidth) * 100;

      setFinalRightValue(finalRightPercent);
    }
  }, [frontpageContainer]);

  // UseEffect to update startDelay to 3.62s after the video has loaded
  useEffect(() => {
    const video = videoElement.current;
    if (!video) return;

    const handleLoadedData = () => {
      if (video.duration) {
        setStartDelay(isMobile ? 0 : 3.62);
        setVideoLoaded(true);
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    // Manually trigger if it already loaded before this effect ran. Happens on the front page sometimes.
    if (video.readyState >= 3) {
      handleLoadedData();
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, [videoElement, setVideoLoaded, isMobile]);

  useEffect(() => {
    if (!videoElement.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(videoElement.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoElement.current;
    if (!video) return;

    if (!isInView && !video.paused) {
      video.pause();
    }

    const isActuallyPlaying =
      video.currentTime > 0 &&
      !video.paused &&
      !video.ended &&
      video.readyState > 2;

    if (isInView && !isActuallyPlaying && !userPaused) {
      video.play().catch(() => {}); // suppress autoplay errors
    }
  }, [isInView, userPaused]);

  return (
    <>
      <Head>
        <link rel='preload' href={(video as Media).url as string} />
      </Head>
      <div
        className={cn(
          styles.wrapper,
          backgroundColor ? `${styles[`wrapper--${backgroundColor}`]}` : ''
        )}
        data-hero
        data-background-color={backgroundColor || 'white'}
      >
        <div className={styles.container}>
          <div
            ref={frontpageContainer}
            className={cn('container', styles.innerContainer)}
          >
            <HeroContent
              className={styles.heroContent}
              ref={heroContentRef}
              startDelay={startDelay}
              {...props}
            />
            {video && videoElement && (
              <motion.div
                className={cn(styles.media)}
                animate={
                  isMobile
                    ? {
                        opacity: [0, 0, 1, 1],
                        y: ['100%', '0', '0', '0'],
                      }
                    : {
                        bottom: ['0%', '0%', '10%', '15%'],
                        width: ['100%', '100%', '90%', '30%'],
                        maxWidth: [
                          'calc(100% - 0px)',
                          'calc(100% - 0px)',
                          'calc(100% - 32px)',
                          'calc(100% - 32px)',
                        ],
                        height: ['100%', '100%', '80%', '71%'],
                        right: ['0', '0', '5%', `${finalRightValue}%`],
                        borderRadius: [
                          '0px',
                          '0px',
                          '0px',
                          isMobile ? '0px' : '12px',
                        ],
                      }
                }
                style={{
                  willChange:
                    'opacity, transform, bottom, width, height, right',
                }}
                transition={{
                  duration: 4.62,
                  delay: 0.2,
                  times: isMobile ? [0, 0.3, 0.6, 1] : [0, 0.4, 0.8, 1],
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <video
                  ref={videoElement}
                  preload='auto'
                  autoPlay
                  loop
                  muted
                  playsInline
                >
                  <source
                    src={(video as Media).url || ''}
                    type={(video as Media).mimeType || ''}
                  />
                  {selectedSource && (
                    <source
                      src={selectedSource.url || ''}
                      type={selectedSource.mimeType || ''}
                    />
                  )}
                </video>
                {videoLoaded && videoElement && (
                  <FrontPagePlayPauseButton
                    videoElement={videoElement.current}
                    setUserPaused={setUserPaused}
                  />
                )}
              </motion.div>
            )}
          </div>
        </div>
        <AnimatedBlob />
      </div>
    </>
  );
};
